import React, { useState, useMemo, useRef } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import portfolioService, { PerformancePosition, BenchmarkHistory } from '../../services/portfolio';
import HoldingsBreakdownChart from './HoldingsBreakdownChart';
import { formatPercentage } from '../../utils/formatters';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import LoadingSpinner from '../ui/LoadingSpinner';

type DateRange = '1M' | '3M' | '6M' | '1Y' | 'YTD';

const DATE_RANGES: { key: DateRange; label: string }[] = [
  { key: '1M', label: '1M' },
  { key: '3M', label: '3M' },
  { key: '6M', label: '6M' },
  { key: '1Y', label: '1Y' },
  { key: 'YTD', label: 'YTD' },
];

const BENCHMARK_PRESETS = [
  { label: 'S&P 500',    ticker: 'SPY'     },
  { label: 'MSCI World', ticker: 'IWDA.AS' },
  { label: 'Nasdaq',     ticker: 'QQQ'     },
  { label: 'DAX',        ticker: '^GDAXI'  },
];

const PORTFOLIO_COLOR = '#000537';
const BENCHMARK_COLOR = '#7c3aed';

const getFromDate = (range: DateRange): string => {
  const d = new Date();
  switch (range) {
    case '1M':  d.setMonth(d.getMonth() - 1); break;
    case '3M':  d.setMonth(d.getMonth() - 3); break;
    case '6M':  d.setMonth(d.getMonth() - 6); break;
    case '1Y':  d.setFullYear(d.getFullYear() - 1); break;
    case 'YTD': d.setMonth(0); d.setDate(1); break;
  }
  return d.toISOString().split('T')[0];
};

const gainColor = (v: number | null) =>
  v == null ? '#71717a' : v >= 0 ? '#16a34a' : '#dc2626';

// Normalize a benchmark price series against the portfolio's first value
const normalizeBenchmark = (
  benchSeries: BenchmarkHistory['series'],
  basePortfolioValue: number,
): Map<string, number> => {
  const map = new Map<string, number>();
  if (!benchSeries.length || !basePortfolioValue) return map;
  const basePrice = benchSeries[0].price;
  if (!basePrice) return map;
  let last = basePrice;
  benchSeries.forEach(({ date, price }) => {
    if (price) last = price;
    map.set(date, (last / basePrice) * basePortfolioValue);
  });
  return map;
};

interface Props {
  portfolioId: number;
}

const PerformanceSection: React.FC<Props> = ({ portfolioId }) => {
  const { isPrivateMode } = usePrivacy();
  const { formatAmount, formatAmountCompact } = useCurrency();
  const [range, setRange]               = useState<DateRange>('1Y');
  // 'none' | preset ticker | 'custom'
  const [dropdownValue, setDropdownValue] = useState<string>('none');
  const [customInput, setCustomInput]     = useState('');
  const [activeTicker, setActiveTicker]   = useState<string | null>(null);
  const customInputRef = useRef<HTMLInputElement>(null);

  const from = useMemo(() => getFromDate(range), [range]);

  const isCustomMode = dropdownValue === 'custom';

  // Main performance query
  const { data, isLoading: perfLoading, error } = useQuery({
    queryKey:  ['portfolio-performance', portfolioId, from],
    queryFn:   () => portfolioService.getPortfolioPerformance(portfolioId, from),
    enabled:   !!portfolioId,
    staleTime: 5 * 60 * 1000,
  });

  // Pre-fetch all preset benchmarks in parallel
  const presetQueries = useQueries({
    queries: BENCHMARK_PRESETS.map(preset => ({
      queryKey:  ['benchmark-history', preset.ticker, from],
      queryFn:   () => portfolioService.getBenchmarkHistory(preset.ticker, from),
      staleTime: 15 * 60 * 1000,
    })),
  });

  // Custom benchmark query — only fires once the user submits a ticker
  const { data: customData, isLoading: customLoading } = useQuery({
    queryKey:  ['benchmark-history', activeTicker, from],
    queryFn:   () => portfolioService.getBenchmarkHistory(activeTicker!, from),
    enabled:   isCustomMode && !!activeTicker,
    staleTime: 15 * 60 * 1000,
  });

  const handleShowCustom = () => {
    setDropdownValue('custom');
    setActiveTicker(null);
    setTimeout(() => customInputRef.current?.focus(), 0);
  };

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = customInput.trim().toUpperCase();
    if (t) setActiveTicker(t);
  };

  // Resolve active benchmark data from either presets or custom query
  const activeBenchmarkData: BenchmarkHistory | undefined = useMemo(() => {
    if (!activeTicker) return undefined;
    const presetIdx = BENCHMARK_PRESETS.findIndex(p => p.ticker === activeTicker);
    if (presetIdx >= 0) return presetQueries[presetIdx]?.data;
    return customData;
  }, [activeTicker, presetQueries, customData]);

  // Merge benchmark into chart series
  const chartData = useMemo(() => {
    const series = data?.series ?? [];
    if (!series.length || !activeBenchmarkData?.series.length) return series;
    const baseValue = series[0].value;
    const benchMap  = normalizeBenchmark(activeBenchmarkData.series, baseValue);
    return series.map(point => ({ ...point, benchmark: benchMap.get(point.date) }));
  }, [data?.series, activeBenchmarkData]);

  const hasBenchmark = chartData.some(p => (p as any).benchmark != null);

  const benchmarkLabel = useMemo(() => {
    if (!activeTicker) return null;
    return BENCHMARK_PRESETS.find(p => p.ticker === activeTicker)?.label ?? activeTicker;
  }, [activeTicker]);

  if (perfLoading) return <LoadingSpinner size="md" text="Loading performance data..." />;
  if (error || !data) {
    return <div className="text-center p-4" style={{ color: '#dc2626' }}>Could not load performance data</div>;
  }

  const { summary, positions } = data;
  const etfPositions   = positions.filter(p => p.type === 'etf');
  const assetPositions = positions.filter(p => p.type === 'asset');

  const kpiCards = [
    { label: 'Cost Basis',        value: summary.cost_basis,    format: 'currency' as const, colored: false },
    { label: 'Current Value',     value: summary.current_value, format: 'currency' as const, colored: false },
    { label: 'Total Gain / Loss', value: summary.absolute_gain, format: 'currency' as const, colored: true  },
    { label: 'Total Return',      value: summary.return_pct,    format: 'percent'  as const, colored: true  },
  ];

  const rangeChipStyle = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px', fontSize: '0.75rem', fontWeight: active ? 600 : 400,
    borderRadius: '4px', border: 'none',
    background: active ? '#000537' : 'transparent',
    color: active ? '#fff' : '#71717a',
    cursor: 'pointer',
  });

  const benchChipStyle = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px', fontSize: '0.75rem', fontWeight: active ? 600 : 400,
    borderRadius: '4px', border: 'none',
    background: active ? BENCHMARK_COLOR : 'transparent',
    color: active ? '#fff' : '#71717a',
    cursor: 'pointer',
  });

  return (
    <div className="d-flex flex-column gap-4">
      {/* KPI summary */}
      <div className="row g-3">
        {kpiCards.map(card => (
          <div key={card.label} className="col-6 col-md-3">
            <div className="card card-forest h-100">
              <div className="card-body p-3">
                <div style={{ fontSize: '0.75rem', color: '#71717a', marginBottom: '4px' }}>{card.label}</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 600, color: card.colored ? gainColor(card.value) : '#18181b' }}>
                  {card.value == null
                    ? '—'
                    : card.format === 'percent'
                    ? `${card.value >= 0 ? '+' : ''}${formatPercentage(card.value)}`
                    : (card.colored && card.value > 0 ? '+' : '') + formatAmount(card.value, isPrivateMode)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts row — portfolio value + holdings breakdown side by side */}
      <div className="row g-3">
      <div className="col-12 col-xl-6">
      {/* Chart card */}
      <div className="card card-forest h-100">
        <div className="card-body p-3 p-md-4">

          {/* Row 1: title + date range */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="card-title mb-0 fw-semibold text-forest-600">Portfolio Value</h5>
            <div className="d-flex gap-1">
              {DATE_RANGES.map(r => (
                <button key={r.key} onClick={() => setRange(r.key)} style={rangeChipStyle(range === r.key)}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2: benchmark buttons, right-aligned */}
          <div className="d-flex justify-content-end align-items-center gap-1 mb-3 flex-wrap">
            {BENCHMARK_PRESETS.map(p => (
              <button
                key={p.ticker}
                onClick={() => {
                  if (dropdownValue === p.ticker) {
                    setDropdownValue('none'); setActiveTicker(null);
                  } else {
                    setDropdownValue(p.ticker); setActiveTicker(p.ticker); setCustomInput('');
                  }
                }}
                style={benchChipStyle(dropdownValue === p.ticker)}
              >
                {p.label}
              </button>
            ))}
            {isCustomMode && activeTicker ? (
              <button onClick={() => { setDropdownValue('none'); setActiveTicker(null); setCustomInput(''); }} style={benchChipStyle(true)}>
                {activeTicker} ✕
              </button>
            ) : (
              <button onClick={handleShowCustom} style={benchChipStyle(false)}>
                Custom…
              </button>
            )}
            {isCustomMode && !activeTicker && (
              <form onSubmit={handleCustomSubmit} className="d-flex gap-1 align-items-center">
                <input
                  ref={customInputRef}
                  value={customInput}
                  onChange={e => setCustomInput(e.target.value)}
                  placeholder="e.g. AAPL"
                  className="form-control form-control-sm"
                  style={{ width: 90, fontSize: '0.75rem' }}
                />
                <button type="submit" style={benchChipStyle(false)}>Go</button>
              </form>
            )}
            {isCustomMode && customLoading && activeTicker && (
              <span style={{ fontSize: '0.75rem', color: '#71717a' }}>Loading…</span>
            )}
          </div>

          {/* Chart */}
          {chartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={d => {
                      const dt = new Date(d);
                      return `${dt.toLocaleString('default', { month: 'short' })} '${String(dt.getFullYear()).slice(2)}`;
                    }}
                    tick={{ fontSize: 11, fill: '#71717a' }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#71717a' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={v => formatAmountCompact(v, isPrivateMode)}
                    width={52}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const portfolioVal  = payload.find(p => p.dataKey === 'value')?.value as number | undefined;
                      const benchmarkVal  = payload.find(p => p.dataKey === 'benchmark')?.value as number | undefined;
                      return (
                        <div style={{ background: '#fff', border: '1px solid #f4f4f5', borderRadius: '8px', padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: '0.8rem' }}>
                          <div style={{ color: '#71717a', marginBottom: '4px' }}>{label}</div>
                          {portfolioVal != null && (
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: PORTFOLIO_COLOR, flexShrink: 0, display: 'inline-block' }} />
                              <span style={{ color: '#52525b' }}>Portfolio</span>
                              <span style={{ fontWeight: 600, color: '#18181b', marginLeft: 'auto', paddingLeft: 12 }}>
                                {formatAmount(portfolioVal, isPrivateMode)}
                              </span>
                            </div>
                          )}
                          {benchmarkVal != null && (
                            <div className="d-flex align-items-center gap-2 mt-1">
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: BENCHMARK_COLOR, flexShrink: 0, display: 'inline-block' }} />
                              <span style={{ color: '#52525b' }}>{benchmarkLabel} (indexed)</span>
                              <span style={{ fontWeight: 600, color: '#18181b', marginLeft: 'auto', paddingLeft: 12 }}>
                                {formatAmount(benchmarkVal, isPrivateMode)}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  {summary.cost_basis != null && (
                    <ReferenceLine
                      y={summary.cost_basis}
                      stroke="#a1a1aa"
                      strokeDasharray="4 2"
                      label={{ value: 'Cost basis', position: 'insideTopLeft', fontSize: 10, fill: '#a1a1aa' }}
                    />
                  )}
                  <Line type="monotone" dataKey="value" stroke={PORTFOLIO_COLOR} strokeWidth={2} dot={false} />
                  {hasBenchmark && (
                    <Line type="monotone" dataKey="benchmark" stroke={BENCHMARK_COLOR} strokeWidth={2} dot={false} strokeDasharray="5 3" connectNulls />
                  )}
                </LineChart>
              </ResponsiveContainer>

              {hasBenchmark && (
                <div className="d-flex gap-3 mt-2">
                  <span className="d-flex align-items-center gap-1" style={{ fontSize: '0.75rem', color: '#52525b' }}>
                    <span style={{ width: 16, height: 2, background: PORTFOLIO_COLOR, display: 'inline-block' }} />
                    Portfolio
                  </span>
                  <span className="d-flex align-items-center gap-1" style={{ fontSize: '0.75rem', color: '#52525b' }}>
                    <span style={{ width: 16, height: 2, background: BENCHMARK_COLOR, display: 'inline-block' }} />
                    {benchmarkLabel} (indexed)
                  </span>
                </div>
              )}
            </>
          ) : (
            <div style={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a1a1aa', fontSize: '0.875rem' }}>
              Historical price data unavailable — add Yahoo tickers to your ETFs to enable the chart
            </div>
          )}
        </div>
      </div>
      </div>{/* col */}

      <div className="col-12 col-xl-6">
        {data.series.length > 0 && positions.length > 0
          ? <HoldingsBreakdownChart portfolioSeries={data.series} positions={positions} />
          : <div className="card card-forest h-100"><div className="card-body p-3 p-md-4 d-flex align-items-center justify-content-center" style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>No holdings data available</div></div>
        }
      </div>
      </div>{/* row */}

      {/* Positions table */}
      {positions.length > 0 && (
        <div className="card card-forest">
          <div className="card-body p-3 p-md-4">
            <h5 className="card-title mb-3 fw-semibold text-forest-600">Positions</h5>
            {etfPositions.length > 0 && (
              <PositionsTable label="ETF Investments" positions={etfPositions} isPrivateMode={isPrivateMode} />
            )}
            {assetPositions.length > 0 && (
              <PositionsTable label="Direct Investments" positions={assetPositions} isPrivateMode={isPrivateMode} className={etfPositions.length > 0 ? 'mt-4' : ''} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface PositionsTableProps {
  label: string;
  positions: PerformancePosition[];
  isPrivateMode: boolean;
  className?: string;
}

const PositionsTable: React.FC<PositionsTableProps> = ({ label, positions, isPrivateMode, className = '' }) => {
  const { formatAmount } = useCurrency();
  return (
  <div className={className}>
    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#71717a', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div className="table-responsive">
      <table className="table table-sm mb-0" style={{ fontSize: '0.875rem', tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          <col style={{ width: '28%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '9%' }} />
        </colgroup>
        <thead>
          <tr style={{ color: '#71717a', borderBottom: '1px solid #f4f4f5' }}>
            <th className="fw-normal ps-0">Name</th>
            <th className="fw-normal text-end">Shares</th>
            <th className="fw-normal text-end">Avg Cost</th>
            <th className="fw-normal text-end">Price</th>
            <th className="fw-normal text-end">Cost Basis</th>
            <th className="fw-normal text-end">Value</th>
            <th className="fw-normal text-end">Gain / Loss</th>
            <th className="fw-normal text-end pe-0">Return</th>
          </tr>
        </thead>
        <tbody>
          {positions.map(pos => (
            <tr key={`${pos.type}-${pos.id}`} style={{ borderBottom: '1px solid #f9f9f9' }}>
              <td className="ps-0">
                <div style={{ fontWeight: 500 }}>{pos.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#71717a' }}>{pos.ticker}</div>
              </td>
              <td className="text-end">{pos.shares.toLocaleString('en-US', { maximumFractionDigits: 4 })}</td>
              <td className="text-end">{pos.purchase_price != null ? formatAmount(pos.purchase_price, isPrivateMode) : '—'}</td>
              <td className="text-end">{formatAmount(pos.current_price, isPrivateMode)}</td>
              <td className="text-end">{pos.cost_basis != null ? formatAmount(pos.cost_basis, isPrivateMode) : '—'}</td>
              <td className="text-end">{formatAmount(pos.current_value, isPrivateMode)}</td>
              <td className="text-end" style={{ color: gainColor(pos.absolute_gain) }}>
                {pos.absolute_gain != null ? (pos.absolute_gain >= 0 ? '+' : '') + formatAmount(pos.absolute_gain, isPrivateMode) : '—'}
              </td>
              <td className="text-end pe-0" style={{ color: gainColor(pos.return_pct) }}>
                {pos.return_pct != null ? `${pos.return_pct >= 0 ? '+' : ''}${formatPercentage(pos.return_pct)}` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
  );
};

export default PerformanceSection;
